import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, Package, Gift, Megaphone, ShieldCheck, LogOut, Menu, X, UserCog, ToggleRight, CreditCard, Coins, Crown, BellRing, Plug } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { firstAllowedRoute } from "@/components/ProtectedRoute";

// perm: uno o varios permisos; la entrada se muestra si la persona tiene alguno.
type NavItem = { to: string; label: string; icon: React.ComponentType<any>; testid: string; perm: string | string[]; end?: boolean };

// Menú agrupado por la tarea que se viene a hacer: operación diaria arriba, configuración al final.
const groups: { title: string | null; items: NavItem[] }[] = [
  { title: null, items: [
    { to: "/admin", label: "Inicio", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard", perm: "dashboard" },
  ] },
  { title: "Clientes", items: [
    // Incluye la carga de documentos (cédulas autorizadas) y el historial de aprobaciones.
    { to: "/admin/usuarios", label: "Gestión de clientes", icon: Users, testid: "admin-nav-users", perm: "users" },
    { to: "/admin/suscripciones", label: "Suscripciones", icon: CreditCard, testid: "admin-nav-subscriptions", perm: "subscriptions" },
  ] },
  { title: "Puntos y premios", items: [
    // Entrega (a un cliente, masiva, historial) y asignación (reglas de puntos).
    { to: "/admin/puntos", label: "Puntos", icon: Coins, testid: "admin-nav-points", perm: ["users", "rules"] },
    { to: "/admin/canjes", label: "Canjes", icon: Package, testid: "admin-nav-redemptions", perm: "redemptions" },
    { to: "/admin/catalogo", label: "Catálogo de premios", icon: Gift, testid: "admin-nav-rewards", perm: "catalog" },
  ] },
  { title: "Comunicaciones", items: [
    { to: "/admin/campanas", label: "Notificaciones push", icon: Megaphone, testid: "admin-nav-campaigns", perm: "campaigns" },
    { to: "/admin/notificaciones", label: "Notificaciones automáticas", icon: BellRing, testid: "admin-nav-notifications", perm: "campaigns" },
  ] },
  { title: "Configuración del programa", items: [
    { to: "/admin/niveles", label: "Niveles y beneficios", icon: Crown, testid: "admin-nav-tiers", perm: "rules" },
    { to: "/admin/funcionalidades", label: "Secciones del portal", icon: ToggleRight, testid: "admin-nav-features", perm: "portal" },
    { to: "/admin/odoo", label: "Integración con Odoo", icon: Plug, testid: "admin-nav-odoo", perm: "users" },
    { to: "/admin/equipo", label: "Equipo y permisos", icon: UserCog, testid: "admin-nav-team", perm: "team" },
  ] },
];
const nav = groups.flatMap((g) => g.items);
const canSee = (n: NavItem, perms: string[]) => (Array.isArray(n.perm) ? n.perm : [n.perm]).some((p) => perms.includes(p));

// El menú lateral es un panel deslizable por debajo de 1024 px.
function useIsDesktop() {
  const q = "(min-width: 1024px)";
  const [is, setIs] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const h = () => setIs(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return is;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const perms = user?.permissions || [];
  const visibleGroups = groups.map((g) => ({ ...g, items: g.items.filter((n) => canSee(n, perms)) })).filter((g) => g.items.length);
  const current = nav.find((n) => matchPath({ path: n.to, end: !!n.end }, location.pathname) || (!n.end && location.pathname.startsWith(n.to + "/")));

  useEffect(() => {
    // Staff sin permiso de Inicio: redirigir a su primera sección permitida.
    // Sin ninguna, RequirePermission muestra la página "Sin acceso".
    if (location.pathname === "/admin" && !perms.includes("dashboard")) {
      const first = firstAllowedRoute(perms);
      if (first && first !== "/admin") navigate(first, { replace: true });
    }
  }, [location.pathname, perms]); // eslint-disable-line

  // Menú móvil: Escape lo cierra y devuelve el foco al botón; al abrir, el foco entra al menú.
  useEffect(() => {
    if (!open) return;
    navRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); toggleRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const doLogout = () => { logout(); navigate("/"); };
  const hidden = !isDesktop && !open;

  return (
    <div className="h-dvh overflow-hidden bg-obsidian text-slate-900 flex">
      <a href="#admin-main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow">
        Saltar al contenido
      </a>
      <aside
        id="admin-sidebar"
        aria-label="Menú del panel"
        inert={hidden ? true : undefined}
        aria-hidden={hidden || undefined}
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-obsidian-card border-r border-slate-200 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-200">
          <Link to="/admin" aria-label="Ir al inicio del panel"><BrandLogo tag="Admin" /></Link>
        </div>
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-gold-ink" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-800 font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-600 truncate">{user?.role === "admin" ? "Administrador principal" : "Miembro del equipo"}</p>
          </div>
        </div>
        <nav ref={navRef} aria-label="Secciones del panel" className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
          {visibleGroups.map((g, gi) => (
            <div key={gi} role="group" aria-labelledby={g.title ? `nav-group-${gi}` : undefined}>
              {g.title && <p id={`nav-group-${gi}`} className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{g.title}</p>}
              <div className="space-y-1">
                {g.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testid}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? "bg-gold/10 text-gold-ink font-medium border border-gold/30" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      }`
                    }
                  >
                    <n.icon style={{ width: 18, height: 18 }} aria-hidden="true" />
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <Button data-testid="admin-logout-button" variant="ghost" onClick={doLogout} className="w-full justify-start text-slate-600 hover:text-red-700 hover:bg-red-500/10">
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />}

      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <header className="h-16 lg:hidden flex items-center gap-3 px-4 border-b border-slate-200 bg-obsidian-card shrink-0">
          <button
            ref={toggleRef}
            onClick={() => setOpen(!open)}
            className="text-slate-700 p-2 -ml-2 rounded-lg"
            data-testid="admin-mobile-toggle"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            aria-controls="admin-sidebar"
          >
            {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
          <span className="font-medium text-slate-800 truncate" data-testid="admin-mobile-section">{current?.label || "Panel"}</span>
        </header>
        <main id="admin-main" tabIndex={-1} className="flex-1 overflow-y-auto p-5 sm:p-8 focus:outline-none">
          <div className="max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
